import React, { useEffect, useState } from "react";

import { IconButton, Tooltip } from "@mui/material";
import { DirectionsBoatOutlined as DirectionsBoatOutlinedIcon } from "@mui/icons-material";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { FiEdit, FiLink2, FiTrash2 } from "react-icons/fi";
import { IoAirplaneOutline } from "react-icons/io5";
import { RiTruckLine } from "react-icons/ri";
import { TiWarningOutline } from "react-icons/ti";

import { useVideoSurveyInventory } from "../../../../../hooks/useVideoSurveyInventory";
import { Destination } from "../../../../../models/destination";
import { Location, DifferentLocation as LocationAddress } from "../../../../../models/location";
import { Material } from "../../../../../models/material";
import { QuotedItem, RoomJob } from "../../../../../models/sales";
import { getColorByDestination } from "../../../../../util/getColorByDestination";
import { getIconByModeOrDestination } from "../../../../../util/getDestinationIcon";
import { getMeasurement } from "../../../../../util/getMeasurement";
import InputRange from "../../../../Form/InputRange";
import EditItemModal from "../../../../Generic/QuotedInventory/modals/EditItemModal";
import EditMaterialModal from "../../../../Generic/QuotedInventory/modals/EditMaterialModal";
import useStyles from "./inventoryListValuesStyles";

interface InventoryListValuesProps {
  room: RoomJob;
  destinations: Destination[];
  locations: LocationAddress[];
  items: QuotedItem[];
  materials: Material[];
}

export const InventoryListValues = ({
  room,
  destinations,
  locations,
  items,
  materials,
}: InventoryListValuesProps) => {
  const measurement = getMeasurement();

  const {
    handleDeleteItem,
    handleUpdateItem,
    handleDeleteMaterial,
    handleUpdateMaterial,
    updateEnabled,
    setUpdateEnabled,
    packingMaterials,
  } = useVideoSurveyInventory();

  const { t: translate } = useTranslation();

  const classes = useStyles();

  const [openItemModal, setOpenItemModal] = useState<boolean>(false);
  const [openMaterialModal, setOpenMaterialModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<QuotedItem | null>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>(-1);
  const [selectedMaterialIndex, setSelectedMaterialIndex] = useState<number>(-1);
  const [itemList, setItemList] = useState<QuotedItem[]>(items);
  const [materialList, setMaterialList] = useState<Material[]>([]);
  const [typingTimeout, setTypingTimeout] = useState<any>(0);

  const onUpdateQuantityItem = (value: number, index: number) => {
    if (typingTimeout) clearTimeout(typingTimeout);
    const data = [...itemList];

    data[index].itemJob.quantity = value;
    if (!updateEnabled) setUpdateEnabled(true);
    setTypingTimeout(
      setTimeout(() => {
        handleUpdateItem(room.name, index, data[index]);
      }, 500)
    );
  };

  const onUpdateVolumeItem = (value: number, index: number) => {
    if (typingTimeout) clearTimeout(typingTimeout);
    const data = [...itemList];

    if (measurement?.code === "ft3") data[index].itemJob.volume = Number(value) / 35.315;
    else data[index].itemJob.volume = value;

    if (!updateEnabled) setUpdateEnabled(true);

    setTypingTimeout(
      setTimeout(() => {
        handleUpdateItem(room.name, index, data[index]);
      }, 500)
    );
  };

  const getDestinationIcon = (
    mode?: string,
    destination?: Destination,
    destinationResidence?: Location
  ) => {
    switch (getIconByModeOrDestination(mode, destination, destinationResidence)) {
      case "TiWarningOutline":
        return (
          <TiWarningOutline size="20px" className={classnames("MuiSvgIcon-root", classes.icon)} />
        );
      case "RiTruckLine":
        return <RiTruckLine size="20px" className={classnames("MuiSvgIcon-root", classes.icon)} />;
      case "DirectionsBoatOutlinedIcon":
        return <DirectionsBoatOutlinedIcon className={classes.icon} />;
      case "IoAirplaneOutline":
        return (
          <IoAirplaneOutline size="20px" className={classnames("MuiSvgIcon-root", classes.icon)} />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    setItemList(items);
  }, [items]);

  useEffect(() => {
    setMaterialList(materials);
  }, [materials]);

  return (
    <div className="mt-3">
      <div className="itemsList">
        <header className={classes.gridList}>
          <span className="item listHeaderItem">{translate("item")}</span>

          <span className="qty listHeaderItem">{translate("qty")}</span>

          <span className="volume listHeaderItem">{translate("volume")}</span>

          <span className="action listHeaderItem">{translate("actions")}</span>
        </header>

        <section data-cy="roomItemList" className={classes.gridListBody}>
          {itemList.map((item: QuotedItem, index: number) => {
            return (
              <div className={classes.gridList} key={item.itemJob.id || index}>
                <span
                  className="item d-flex align-items-center"
                  style={{
                    color: getColorByDestination(
                      item?.itemJob?.destination,
                      item?.itemJob?.destinationResidence
                    ),
                  }}
                >
                  {getDestinationIcon(
                    item?.itemJob?.mode,
                    item?.itemJob?.destination,
                    item?.itemJob?.destinationResidence
                  )}
                  {item?.itemJob?.description || ""}
                </span>
                <span className="qty d-flex justify-content-center">
                  <InputRange
                    containerClassName={classes.containerInputRange}
                    value={item?.itemJob?.quantity || 1}
                    onIncrease={(value) => onUpdateQuantityItem(value, index)}
                    onDecrease={(value) => onUpdateQuantityItem(value, index)}
                    change={(value) => onUpdateQuantityItem(value, index)}
                    range={1}
                    onlyIntValues
                  />
                </span>
                <span className="volume d-flex justify-content-center">
                  <InputRange
                    containerClassName={classes.containerInputRange}
                    measurementInputRange={measurement}
                    value={item?.itemJob?.volume || 0}
                    onIncrease={(value) => onUpdateVolumeItem(value, index)}
                    onDecrease={(value) => onUpdateVolumeItem(value, index)}
                    change={(value) => {
                      const nValue = Number(value);

                      if (!isNaN(nValue)) onUpdateVolumeItem(nValue, index);
                    }}
                    minValue={0}
                  />
                </span>
                <div className={`action ${classes.bodyListButtons}`}>
                  <IconButton
                    onClick={() => {
                      handleDeleteItem(room.name, index);
                    }}
                  >
                    <FiTrash2 size="20px" color="#F97066" />
                  </IconButton>

                  <IconButton
                    onClick={() => {
                      setSelectedItem(item);
                      setSelectedItemIndex(index);
                      setOpenItemModal(true);
                    }}
                  >
                    <FiEdit size="20px" color="#475467" />
                  </IconButton>
                </div>
              </div>
            );
          })}
        </section>
      </div>
      <div className="materialList mt-3">
        {materialList && materialList?.length > 0 && (
          <header className={classes.gridList}>
            <span className="listHeaderItem">{translate("material")}</span>
          </header>
        )}

        <section data-cy="roomMaterialList" className={classes.gridListBody}>
          {materialList?.map((material: Material, index: number) => (
            <div className={classes.gridList} key={index}>
              <span
                className="item d-flex align-items-center"
                style={{
                  color: getColorByDestination(
                    material?.destination,
                    material?.destinationResidence
                  ),
                }}
              >
                {getDestinationIcon(
                  material?.mode,
                  material?.destination,
                  material?.destinationResidence
                )}
                {material?.serviceBooking?.name || ""}
                {material?.itemJobId && (
                  <Tooltip
                    placement="top"
                    title={`${translate("linkedTo")} ${material?.itemJob?.description}`}
                  >
                    <div className={classes.containerIconLink}>
                      <FiLink2 className={classes.ml12} color="#0055A4" />
                    </div>
                  </Tooltip>
                )}
              </span>

              <span className="qty d-flex justify-content-center">
                <InputRange
                  containerClassName={classes.containerInputRange}
                  value={Number(material.quantity)}
                  disabled={!!material?.itemJobId}
                  change={(value: number) => {
                    if (typingTimeout) clearTimeout(typingTimeout);

                    const data = materials;

                    data[index].quantity = value;

                    if (!updateEnabled) setUpdateEnabled(true);

                    setTypingTimeout(
                      setTimeout(() => {
                        handleUpdateMaterial(room.name, index, data[index]);
                      }, 500)
                    );
                  }}
                  range={1}
                  onlyIntValues
                />
              </span>

              <span className="volume d-flex justify-content-center">
                <InputRange
                  containerClassName={classes.containerInputRange}
                  measurementInputRange={measurement}
                  value={material?.volume}
                  disabled={!!material?.itemJobId}
                  change={(value: number) => {
                    if (typingTimeout) clearTimeout(typingTimeout);

                    const data = materials;

                    if (measurement?.code === "ft3") data[index].volume = Number(value) / 35.3146;
                    else data[index].volume = value;

                    if (!updateEnabled) setUpdateEnabled(true);

                    setTypingTimeout(
                      setTimeout(() => {
                        handleUpdateMaterial(room.name, index, data[index]);
                      }, 500)
                    );
                  }}
                  minValue={0}
                />
              </span>
              {material?.itemJobId ? (
                <></>
              ) : (
                <div className={`action ${classes.bodyListButtons}`}>
                  <IconButton
                    onClick={() => {
                      handleDeleteMaterial(room.name, index);
                    }}
                  >
                    <FiTrash2 size="20px" color="#F97066" />
                  </IconButton>

                  <IconButton
                    onClick={() => {
                      setSelectedMaterial(material);
                      setSelectedMaterialIndex(index);
                      setOpenMaterialModal(true);
                    }}
                  >
                    <FiEdit size="20px" color="#475467" />
                  </IconButton>
                </div>
              )}
            </div>
          ))}
        </section>
      </div>
      <EditItemModal
        oldQuotedItem={selectedItem}
        openModal={openItemModal}
        locations={locations}
        destinations={destinations || []}
        closeModal={() => {
          setOpenItemModal(false);
          setSelectedItem(null);
        }}
        submit={(value: QuotedItem) => {
          const data = items;

          data[selectedItemIndex] = value;

          handleUpdateItem(room.name, selectedItemIndex, value);
        }}
        packingMaterials={packingMaterials}
      />
      <EditMaterialModal
        material={selectedMaterial}
        openModal={openMaterialModal}
        locations={locations}
        destinations={destinations || []}
        closeModal={() => {
          setOpenMaterialModal(false);
          setSelectedMaterial(null);
        }}
        submit={(value: Material) => {
          if (selectedMaterialIndex !== -1) {
            handleUpdateMaterial(room.name, selectedMaterialIndex, value);
          }
        }}
      />
    </div>
  );
};
